import { P } from "apparatus/library/common";
import { UpdateAppearance } from "command/basic";
import { commandProcessor, Sequence } from "command/command";
import { Move } from "editing/move";
import { clog } from "log";
import { Key, Tool } from "paper";
import appStore from "store/store";
export class PathEditTool extends Tool {
    constructor(app) {
        super();
        this.app = app;
        this.onMouseDown = (event) => {
            const TOLERANCE = 10 / paper.project.view.scaling.x;
            // Get first selected item.
            const selectedFirstItem = appStore.selectedItems()[0];
            if (!selectedFirstItem || selectedFirstItem.type !== "__live edit__")
                return;
            const component = this.app.getApparatusComponent(selectedFirstItem.id);
            if (!component)
                return;
            if (Key.modifiers.alt && Key.modifiers.shift && Key.modifiers.control) {
                const path = getPath(component);
                const delta = event.downPoint.subtract(P(component.props.x, component.props.y));
                path.segments.forEach(s => { s.point = s.point.subtract(delta); });
                this.editedLiveEditComponent = component;
                this.op = { type: "set-center", delta: delta };
            }
            else if (Key.modifiers.shift && Key.modifiers.control) {
                // Align centers.
                const destination = P(component.props.x, component.props.y);
                commandProcessor.execute(new Sequence(appStore.selectedItems().map(i => new Move([i.id], destination.subtract(P(i.x, i.y))))));
            }
            else if (Key.modifiers.alt) {
                const hitResult = component.item.hitTest(event.downPoint, { segments: true, tolerance: TOLERANCE });
                if (hitResult) {
                    this.editedLiveEditComponent = component;
                    if (hitResult.segment.handleIn.length == 0 && hitResult.segment.handleOut.length == 0) {
                        // Straight point. Switch to having both handles
                        hitResult.segment.handleIn = P(20, 0);
                        hitResult.segment.handleOut = P(-20, 0);
                    }
                    else if (hitResult.segment.handleIn.length == 0) {
                        // Only has handleOut. Switch to handleIn.
                        hitResult.segment.handleIn = hitResult.segment.handleOut.multiply(-1);
                        hitResult.segment.handleOut = P(0, 0);
                    }
                    else if (hitResult.segment.handleOut.length == 0) {
                        // Only has handleIn set. Switch to none.
                        hitResult.segment.handleOut = P(0, 0);
                        hitResult.segment.handleIn = P(0, 0);
                    }
                    else {
                        // Has both handles, switch to handleOut only.
                        hitResult.segment.handleIn = P(0, 0);
                    }
                }
            }
            else if (Key.modifiers.shift) {
                // Add a point to path.
                const hitResult = component.item.hitTest(event.downPoint, { stroke: true, tolerance: TOLERANCE });
                clog(hitResult);
                if (hitResult && hitResult.location) {
                    const path = getPath(component);
                    path.insert(hitResult.location.index + 1, event.point.subtract(P(component.props.x, component.props.y)));
                    this.editedLiveEditComponent = component;
                }
            }
            else if (Key.modifiers.control) {
                // Remove a point.
                const hitResult = component.item.hitTest(event.downPoint, { segments: true, tolerance: TOLERANCE });
                if (hitResult) {
                    const path = getPath(component);
                    path.removeSegment(hitResult.segment.index);
                    this.editedLiveEditComponent = component;
                }
            }
            else {
                const hitResult = component.item.hitTest(event.downPoint, {
                    segments: true,
                    handles: true,
                    tolerance: TOLERANCE,
                });
                if (hitResult) {
                    if (hitResult.type === "segment") {
                        this.op = {
                            type: "move-segment",
                            segment: hitResult.segment
                        };
                        this.editedLiveEditComponent = component;
                    }
                    else if (hitResult.type === "handle-in") {
                        this.op = {
                            type: "move-handle-in",
                            segment: hitResult.segment
                        };
                        this.editedLiveEditComponent = component;
                    }
                    else if (hitResult.type === "handle-out") {
                        this.op = {
                            type: "move-handle-out",
                            segment: hitResult.segment
                        };
                        this.editedLiveEditComponent = component;
                    }
                }
            }
        };
        this.onMouseDrag = (event) => {
            if (!this.editedLiveEditComponent)
                return;
            if (!this.op)
                return;
            switch (this.op.type) {
                case "move-segment":
                    this.op.segment.point = this.op.segment.point.add(event.delta);
                    break;
                case "move-handle-in":
                    this.op.segment.handleIn = this.op.segment.handleIn.add(event.delta);
                    if (Key.modifiers.shift) {
                        this.op.segment.handleOut.angle = this.op.segment.handleIn.angle + 180;
                    }
                    break;
                case "move-handle-out":
                    this.op.segment.handleOut = this.op.segment.handleOut.add(event.delta);
                    if (Key.modifiers.shift) {
                        this.op.segment.handleIn.angle = this.op.segment.handleOut.angle + 180;
                    }
                    break;
            }
        };
        this.onMouseUp = (event) => {
            var _a;
            if (!this.editedLiveEditComponent)
                return;
            commandProcessor.startRecording();
            // Extract Path object.
            const path = getPath(this.editedLiveEditComponent);
            const mirrored = this.editedLiveEditComponent.props.appearance["mirrored"] || this.editedLiveEditComponent.props.appearance["mirroredY"];
            const pathData = convertToData(path, mirrored ? path.segments.length / 2 : path.segments.length);
            commandProcessor.execute(new UpdateAppearance(this.editedLiveEditComponent.id, "segments", pathData, this.editedLiveEditComponent.props.appearance["segments"]));
            if (((_a = this.op) === null || _a === void 0 ? void 0 : _a.type) == "set-center") {
                commandProcessor.execute(new Move([this.editedLiveEditComponent.id], this.op.delta));
            }
            commandProcessor.finishRecording();
            this.editedLiveEditComponent = undefined;
            this.op = undefined;
        };
    }
}
function getPath(liveEditComponent) {
    return liveEditComponent.item.children[0].children[0];
}
function convertToData(path, limit) {
    let converted = [];
    let count = 0;
    for (let segment of path.segments) {
        if (count >= limit)
            break;
        const point = P(segment.point.x, segment.point.y);
        const handleOut = segment.handleOut.x == 0 && segment.handleOut.y == 0 ? undefined : P(segment.handleOut.x, segment.handleOut.y);
        const handleIn = segment.handleIn.x == 0 && segment.handleIn.y == 0 ? undefined : P(segment.handleIn.x, segment.handleIn.y);
        if (handleOut) {
            converted.push(`[${toString(point)},${handleIn ? toString(handleIn) : ''},${toString(handleOut)}]`);
        }
        else if (handleIn) {
            converted.push(`[${toString(point)},${toString(handleIn)}]`);
        }
        else {
            converted.push(`[${toString(point)}]`);
        }
        count++;
    }
    return converted.join(",\n");
}
function toString(point) {
    return `[${point.x.round()}, ${point.y.round()}]`;
}
